/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  GamoraController,
  GamoraControllerAbortContextInput,
  GamoraControllerContextIdentifier,
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakServerStateContext,
  HoruzProtocolChulakServerStateContextRegistry,
} from './states/context.js';
import {
  HoruzProtocolChulakServerStateMap,
  HoruzProtocolChulakServerTransitionMap,
} from './statemap.js';
import { HoruzProtocolChulakCloseCode } from './codes.js';
import { HoruzProtocolChulakErrorAgentCancel } from './errors/agent/cancel.error.js';
import { HoruzProtocolChulakErrorProtocolUnknown } from './errors/protocol/unknown.error.js';
import { HoruzProtocolChulakServerStateAuth } from './states/server/auth.state.js';
import { HoruzProtocolChulakServerStateClose } from './states/server/close.state.js';
import { HoruzProtocolChulakServerStateOpen } from './states/server/open.state.js';
import { IHoruz } from '../../horuz.js';
import { WebSocket } from 'ws';
const debug = debugCreate({ namespace: 'horuz:protocols:chulak:server' });

/**
 * @internal
 */
export type HoruzProtocolChulakServerControllerArgs<TRoot extends IHoruz> =
  GamoraFoundationNodeArgs<typeof HoruzProtocolChulakServerStateMap>;

/**
 * HoruzProtocolChulakServerController
 * @public
 */
export class HoruzProtocolChulakServerController<
  TRoot extends IHoruz,
> extends GamoraController<
  typeof HoruzProtocolChulakServerStateMap,
  HoruzProtocolChulakServerStateContext,
  TRoot
> {
  constructor(args: HoruzProtocolChulakServerControllerArgs<TRoot>) {
    assert(
      typeof args === 'object' && args !== null,
      'Invalid args argument: not an object.',
    );

    // Initialize base
    super({
      ...args,
      stateMap: {
        // Used states
        Auth: [HoruzProtocolChulakServerStateAuth, {}],
        Close: [HoruzProtocolChulakServerStateClose, {}],
        Open: [HoruzProtocolChulakServerStateOpen, {}],
      },
      transitionMap: HoruzProtocolChulakServerTransitionMap,
    });
  }

  /**
   * Gets adapter
   */
  get serverSessionStore() {
    return this.$root.$('protocols').$('chulak').$('store').$('serverSession');
  }

  /**
   * Creates context registry
   */
  createContextRegistry(): HoruzProtocolChulakServerStateContextRegistry {
    // Create registry
    return {};
  }

  /**
   * Event
   *
   * Handle request
   */
  handle(ws: WebSocket): GamoraControllerContextIdentifier {
    // Create context
    const contextIdentifier = this.$createContext({
      defaultStateStart: 'Auth',
      defaultStateAbort: 'Close',
    });

    // Create new context
    const context = this.$getContext(contextIdentifier);

    // Create context
    context.stateContext.ws = ws;
    context.stateContext.stateContextRegistry = this.createContextRegistry();

    // set binary type
    ws.binaryType = 'arraybuffer';

    // Setup close event
    ws.on('close', (code) => {
      debug.log(
        '(handle): WebSocket close event for contextIdentifier: ' +
          contextIdentifier +
          ' with code: ' +
          code +
          '.',
      );

      // Transform code to last error
      switch (code) {
        case HoruzProtocolChulakCloseCode.AGENT_CANCEL: {
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAgentCancel.Create(
              'Remote agent cancelled.',
            );
          break;
        }
      }

      // Close the associated context
      this.$tryAbortContext(contextIdentifier, { stateAbort: 'Close' });

      // Clear message queue
      this.$root.$('queues').$('data').empty(contextIdentifier);
    });

    // Setup error event
    ws.on('error', () => {
      debug.log(
        '(handle): WebSocket error event for contextIdentifier: ' +
          contextIdentifier +
          '.',
      );
    });

    // Setup close event
    ws.on('message', (data) => {
      debug.log(
        '(handle): WebSocket message event for contextIdentifier: ' +
          contextIdentifier +
          '.',
      );
      try {
        assert(
          !(data instanceof Array),
          'Invalid message argument: not a valid ArrayBuffer or Buffer.',
        );

        // Convert to uint8 array
        const uint8Array = new Uint8Array(data);

        // Convert message to string
        const isQueued = this.$root
          .$('queues')
          .$('data')
          .queue(contextIdentifier, uint8Array);

        // Failed to process message
        if (!isQueued) {
          debug.log(
            '(handle): Unable to queue message event for contextIdentifier: ' +
              contextIdentifier +
              '.',
          );

          // Set last error
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorProtocolUnknown.Create(
              'Unable to queue message.',
            );
          this.$tryAbortContext(contextIdentifier, {
            stateAbort: 'Close',
          })?.catch((e) => {
            debug.trace(e?.message, e?.stack);
            debug.log(
              '(handle): Unable to transition context to close for contextIdentifier: ' +
                contextIdentifier +
                '.',
            );
          });
        }
      } catch {
        debug.log(
          '(handle): WebSocket unable to handle message event for contextIdentifier: ' +
            contextIdentifier +
            '.',
        );
        ws.close(HoruzProtocolChulakCloseCode.PROTOCOL_UNKNOWN_ERROR);
      }
    });

    return contextIdentifier;
  }
}
